import type { Bar } from './types'
import type { ReplayBus } from './events'
import { createClock, type Clock, type ClockFactory } from './clock'

// The replay core. Holds the full Bar[] in memory; `currentIndex` is the cursor.
// The chart is ever only fed bars[0..currentIndex], so the future is structurally
// un-peekable (there are no future bars in the chart to scroll to).

export interface ReplayEngineDeps {
  bus: ReplayBus
  /** factory so tests can inject a controllable clock */
  clockFactory?: ClockFactory
}

export class ReplayEngine {
  private bars: Bar[] = []
  private index = -1 // index of the last revealed bar
  private playing = false
  private speed = 1
  private readonly bus: ReplayBus
  private readonly clock: Clock

  constructor(deps: ReplayEngineDeps) {
    this.bus = deps.bus
    const factory = deps.clockFactory ?? ((onTick) => createClock(onTick))
    this.clock = factory(() => this.onClockTick())
  }

  loadDataset(bars: Bar[], opts: { startIndex?: number } = {}): void {
    this.pause()
    this.bars = bars
    const start = opts.startIndex ?? Math.min(bars.length - 1, 0)
    this.index = bars.length === 0 ? -1 : Math.min(Math.max(0, start), bars.length - 1)
    this.bus.emit('loaded', { count: bars.length, startIndex: this.index })
  }

  step(): void {
    if (this.index >= this.bars.length - 1) {
      this.handleEnd()
      return
    }
    this.index++
    this.bus.emit('bar:revealed', { index: this.index, bar: this.bars[this.index]! })
  }

  stepBack(): void {
    if (this.index <= 0) return
    const removed = this.index
    this.index--
    this.bus.emit('bar:hidden', { index: removed })
  }

  seekTo(target: number): void {
    if (this.bars.length === 0) return
    const clamped = Math.min(Math.max(0, target), this.bars.length - 1)
    if (clamped === this.index) return
    this.index = clamped
    this.bus.emit('seek', { index: this.index })
  }

  play(barsPerSec?: number): void {
    if (this.playing) return
    if (this.atEnd) return
    if (barsPerSec !== undefined) this.speed = barsPerSec
    this.playing = true
    this.bus.emit('play', { barsPerSec: this.speed })
    this.clock.start(this.speed)
  }

  pause(): void {
    if (!this.playing) return
    this.playing = false
    this.clock.stop()
    this.bus.emit('pause', { index: this.index })
  }

  setSpeed(barsPerSec: number): void {
    this.speed = barsPerSec
    if (this.playing) this.clock.setSpeed(barsPerSec)
  }

  private onClockTick(): void {
    if (this.index >= this.bars.length - 1) {
      this.handleEnd()
      return
    }
    this.step()
  }

  private handleEnd(): void {
    if (this.playing) {
      this.playing = false
      this.clock.stop()
    }
    this.bus.emit('end', { index: this.index })
  }

  // ---- read-only views (never expose bars past currentIndex) ----
  get visibleBars(): readonly Bar[] {
    return this.index < 0 ? [] : this.bars.slice(0, this.index + 1)
  }
  get currentBar(): Bar | null {
    return this.index < 0 ? null : this.bars[this.index]!
  }
  get currentIndex(): number {
    return this.index
  }
  get total(): number {
    return this.bars.length
  }
  get atEnd(): boolean {
    return this.bars.length > 0 && this.index >= this.bars.length - 1
  }
  get isPlaying(): boolean {
    return this.playing
  }
  get speedBps(): number {
    return this.speed
  }
}
